import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCurrentUser } from "@/lib/auth";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { buildReturnToParam } from "@/lib/postLoginRedirect";
import { SERVER_UPDATE_MESSAGE } from "@/features/serverUpdate/maintenanceStatus";
import {
  getServerUpdateServerSnapshot,
  getServerUpdateSnapshot,
  subscribeServerUpdate,
} from "@/features/serverUpdate/serverUpdateStore";

export interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Wartet auf `/me`, bevor die Kinder gerendert werden. Es findet keine
 * Gruppenprüfung statt – die ACL wird serverseitig (page-gate Edge-Function)
 * sowie an den jeweiligen API-Aufrufen erzwungen. Hier sorgen wir nur dafür,
 * dass abhängige Hooks (z. B. ACL-Filter im Header) bereits Nutzerinfos
 * vorfinden und Tipper sofort gerendert werden.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const location = useLocation();
  const { user, loading, error, reload } = useCurrentUser();
  const updating = useSyncExternalStore(
    subscribeServerUpdate,
    () => getServerUpdateSnapshot().active,
    () => getServerUpdateServerSnapshot().active,
  );

  useEffect(() => {
    if (!updating || user) return;
    const id = window.setInterval(() => reload(), 3000);
    return () => window.clearInterval(id);
  }, [updating, user, reload]);

  if (updating && !user) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center px-4 text-center text-label-secondary">
        <div className="flex items-center gap-2 text-sm">
          <Spinner />
          <span>{SERVER_UPDATE_MESSAGE}</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-label-secondary">
        <div className="flex items-center gap-2 text-sm">
          <Spinner />
          <span>Sitzung wird geprüft …</span>
        </div>
      </div>
    );
  }

  if (!user && !error) {
    const returnTo = buildReturnToParam(location.pathname, location.search, location.hash);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (error || !user) {
    return (
      <div className="mx-auto max-w-md py-10">
        <Alert variant="error" title="Sitzung konnte nicht geladen werden">
          {error || "Bitte erneut anmelden."}
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
